import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fileToBase64 } from '../../utils/fileHandling';
import { faArrowLeftLong, faChevronRight,faUpload } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; //direkte Unterstützung für Fußnoten, Durchstreichungen, Tabellen, Aufgabenlisten und URLs
import rehypeKatex from 'rehype-katex'; //um Mathematik darzustellen
import remarkMath from 'remark-math'; //Mathematik in Markdown zu unterstützen
import rehypeRaw from 'rehype-raw'; // HTML in markdown
import rehypeStringify from 'rehype-stringify';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeFormat from 'rehype-format';
import remarkEmoji from 'remark-emoji'; //display short cut emojis
import remarkCollapse from 'remark-collapse'; //not directly typed, make collapsable sections
import { Config } from '../../config/Config';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { useTrackedRun } from '../../hooks/useTrackedRun';
import { AiDisclosureNotice } from '../../components/ui/AiDisclosureNotice';
import { stateIconWaiting } from '../../types/ai/states';
import { useNavigateBack } from '../../hooks/useNavigationBack';

interface GernerationProps {
    'src': string | null,
    'file': any | null;
    'userPrompt':string,
    'assistantResponse':string | null;
};

export const VisionModel = () => {
    const { t } = useTranslation(['apps','toolsApps']);
    const navigateBack = useNavigateBack();
    const [inputs,setInputs] = useState<GernerationProps>({
        src:null,
        file: null,
        userPrompt:t('vision.demoPrompt'),
        assistantResponse:''
    });
    const [promptSended, setPromptSended] = useState(false);
    const baseUrl = Config.json.AiServiceProxyUrl;
    const [state,setState]= useState<null|string>(null);
    const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setInputs(prev => ({
                ...prev,
                ['src']:imgSrc,
                ['file']: file
            }));
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const hanldeSendImageAndPrompt = async() => {
        setPromptSended(true);
        setState('waiting');
        const prompt = inputs.userPrompt ? inputs.userPrompt.trim() : 'Analyse this image and return text'.trim();
        const file = inputs.file;
        if(!file){
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: t('missingImage')
            }));
            setState(null);
            return;
        }
        const base64Image = await fileToBase64(file);
        const body = {
            messages: [
                {
                    role: "user",
                    content: prompt,
                    images: [base64Image]
                }
            ]
        };

        const runHandle = await startRun('vision', { estimatedCost: 0 });
        if (!runHandle) {
            // Either insufficient balance (dialog shown by useTrackedRun) or another failure -
            // fail closed, don't call cProxy without a tracked run.
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.requestFailed')
            }));
            setState(null);
            return;
        }

        try {
            const res = await fetchWithAuth(`${baseUrl}/vision`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });

            if(!res.ok){
                let err = await res.text();
                failRun(runHandle, `Vision request failed (HTTP ${res.status})`);
                setInputs(prev => ({
                    ...prev,
                    ['assistantResponse']: "❌ " + t('error.prefix') + err
                }));
                setState(null);
                return;
            }

            const data = await res.json();
            const usage = data.raw?.usage;
            completeRun(runHandle, {
                inputTokens: usage?.prompt_tokens ?? 0,
                outputTokens: usage?.completion_tokens ?? 0,
            });
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: data.reply || t('noResponse')
            }));
            setState(null);

        } catch(err){
            failRun(runHandle, `Vision request error: ${err}`);
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.connectionError')
            }));
            setState(null);
        }
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.visionModel.name')
    },[]);

    return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid p-4">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    {promptSended  
                        ? state === 'waiting'
                                ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                : <></>
                        : <></>
                    }
                    {promptSended && (
                        <>
                            <div className="row mb-2">
                                <div className='col-auto p-3'>
                                    <div className='row'>
                                        <div className='col-auto h5'>
                                            {t('prompt')}
                                        </div>
                                        <div className='col'>
                                            {inputs.userPrompt}
                                        </div>
                                    </div>
                                    {inputs.src && (
                                        <img
                                            src={inputs.src}
                                            alt={`Image`}
                                            className="rounded d-block border border-secondary rounded p-3 mt-2"
                                            style={{ maxHeight: '600px', width: 'auto' }}
                                        />
                                    )}
                                </div>
                                <div className='col-1'></div>
                                <div className='col  p-3 '>
                                    { inputs.assistantResponse
                                        ?   <>
                                                <div className='row mb-1 h5'>{t('response')}</div>
                                                <div>
                                                    <ReactMarkdown 
                                                        remarkPlugins={[remarkMath,remarkEmoji,remarkGfm, remarkParse,remarkRehype,[remarkCollapse,{test:'heading'}]]} 
                                                        rehypePlugins={[rehypeKatex,rehypeFormat,rehypeRaw, rehypeStringify]}
                                                    >
                                                            {inputs.assistantResponse}
                                                    </ReactMarkdown>
                                                </div>
                                                <AiDisclosureNotice />
                                            </>
                                        : <></>
                                    }
                                </div>
                            </div>
                        </>
                        )}
                </div>
                
                <div className='row mt-2 me-5'>
                    <div className='col-auto'>
                        <label htmlFor="fileuploadImage" className=' fw-bold'>
                            <FontAwesomeIcon icon={faUpload} className='me-2' />
                            {t('uploadImage')}
                        </label>
                        <input
                        id = 'fileuploadImage'
                        type= 'file'
                        accept='image/png,image/jpg,image/jpeg'
                        onChange={event => uploadFile(event)}
                        hidden
                        />
                        {
                            inputs.file
                            ? <p>{inputs.file.name}</p>
                            : <></>
                        }
                    </div>
                    
                </div>
                 <div className="row mt-2">
                    <div className="col">
                        <textarea
                            id="chat-input-vision"
                            className="form-control border border-secondary border-1 bg-transparent"
                            name="userPrompt"
                            placeholder={t('imagePromptPlaceholder')}
                            onChange={handleInputChange}
                            style={{ resize: 'none' }}
                            value={inputs.userPrompt}
                        />
                    </div>
                    <div
                    className="col-auto d-flex align-items-center"
                    onClick={() => hanldeSendImageAndPrompt()}
                    >
                        <button
                            className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={t('send')}
                            disabled={inputs.src == null}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default VisionModel;